import React, { useState, useRef, useEffect } from 'react';

import { FormattedMessage } from 'react-intl';

import Button from '@atlaskit/button/standard-button';
import { ErrorMessage, Label } from '@atlaskit/form';
import { Inline, Stack, xcss } from '@atlaskit/primitives';
import TextArea from '@atlaskit/textarea';

import messages from 'src/components/autoreview-feedback/src/autoreview-feedback.i18n';
import { SendAnalyticsEvent } from 'src/components/conversation/src/components/types';
import {
  actionSubjectIds,
  EventAttributes,
  EventData,
} from 'src/components/conversation/src/internal/analytics';

enum TextSubmissionError {
  VALUE_DOES_NOT_EXIST = 'Please enter a value',
}

type TextSubmissionState = 'hidden' | 'pending' | 'submitted';

type TextFeedbackProps = {
  commonEventData: (additionalAttributes: EventAttributes) => EventData;
  setTextSubmissionState: React.Dispatch<
    React.SetStateAction<TextSubmissionState>
  >;
  sendAnalyticsEvent: SendAnalyticsEvent;
  collapseComment?: () => void;
  isOpen: boolean;
};

/* eslint @typescript-eslint/ban-types: "warn" */
export const TextFeedback: React.FC<TextFeedbackProps> = ({
  commonEventData,
  setTextSubmissionState,
  sendAnalyticsEvent,
  collapseComment,
  isOpen,
}) => {
  const [feedbackBody, setFeedbackBody] = useState<string>('');
  const textArea = useRef<HTMLTextAreaElement>(null);
  const [textSubmissionError, setTextSubmissionError] =
    useState<TextSubmissionError | null>(null);

  const submitTextFeedback = () => {
    if (!feedbackBody) {
      setTextSubmissionError(TextSubmissionError.VALUE_DOES_NOT_EXIST);

      return;
    }

    if (feedbackBody) {
      sendAnalyticsEvent({
        actionSubjectId: actionSubjectIds.autoreviewTextFeedbackSubmission,
        ...commonEventData({
          autoreviewTextFeedback: feedbackBody,
        }),
      });

      setTextSubmissionError(null);
      setTextSubmissionState('submitted');
      setTimeout(() => {
        if (collapseComment) collapseComment();
        setTextSubmissionState('hidden');
      }, 2000);
    }
  };

  const dismissTextFeedback = () => {
    setTextSubmissionState('hidden');
  };

  // focus the textarea when the feedback form is opened
  useEffect(() => {
    if (isOpen && textArea.current) {
      textArea.current.focus();
    }
  }, [isOpen]);

  return (
    <Stack space="space.100" xcss={xcss({ paddingTop: 'space.100' })}>
      <Stack>
        <Label htmlFor="area">
          <FormattedMessage {...messages.textAreaLabel} />
        </Label>
        <TextArea
          value={feedbackBody}
          placeholder="Please add any relevant information or feedback here"
          onChange={e => setFeedbackBody(e.target.value)}
          ref={textArea}
        />
      </Stack>
      {textSubmissionError && (
        <ErrorMessage>{textSubmissionError}</ErrorMessage>
      )}
      <Inline space="space.100">
        <Button appearance="primary" onClick={submitTextFeedback}>
          Submit
        </Button>
        <Button appearance="default" onClick={dismissTextFeedback}>
          Dismiss
        </Button>
      </Inline>
    </Stack>
  );
};
